
import { useState, useEffect } from 'react';
import { Property } from '../lib/supabase';

interface PropertyGalleryModalProps {
  property: Property;
  isOpen: boolean;
  onClose: () => void;
  onInquiryClick: () => void;
}

export default function PropertyGalleryModal({ 
  property, 
  isOpen,
  onClose,
  onInquiryClick
}: PropertyGalleryModalProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [showFullDescription, setShowFullDescription] = useState(false);
  const [showCalculator, setShowCalculator] = useState(false);
  
  const images = property.images && property.images.length > 0 
    ? property.images 
    : property.image 
    ? [property.image]
    : [`https://public.readdy.ai/ai/img_res/4883bbc3fff72839fd8625b98e2f0c83.jpg$%7Bproperty.type.toLowerCase%28%29%7D%20$%7Bproperty.location%7D%20Zagreb%20Croatia%2C%20professional%20real%20estate%20photography&width=1200&height=800&seq=${property.id}&orientation=landscape`];

  // Check if this is a rental property
  const isRental = property.listing_type === 'Najam';
  
  // Get the correct price based on listing type
  const displayPrice = isRental ? property.rental_price : property.price;

  // ESC key handler
  useEffect(() => {
    const handleEsc = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) {
        if (showCalculator) {
          setShowCalculator(false);
        } else {
          onClose();
        }
      }
    };
    
    if (isOpen) {
      document.addEventListener('keydown', handleEsc);
      document.body.style.overflow = 'hidden';
    }
    
    return () => {
      document.removeEventListener('keydown', handleEsc);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, showCalculator, onClose]);

  // Arrow key navigation
  useEffect(() => {
    const handleArrows = (e: KeyboardEvent) => {
      if (!isOpen || showCalculator) return;
      if (e.key === 'ArrowLeft') prevImage();
      if (e.key === 'ArrowRight') nextImage();
    };
    
    document.addEventListener('keydown', handleArrows);
    return () => document.removeEventListener('keydown', handleArrows);
  }, [isOpen, currentImageIndex, showCalculator]);

  const nextImage = () => {
    setCurrentImageIndex((prev) => (prev + 1) % images.length);
  };

  const prevImage = () => {
    setCurrentImageIndex((prev) => (prev - 1 + images.length) % images.length);
  };

  const pricePerSqm = displayPrice && property.area 
    ? Math.round(displayPrice / property.area) 
    : property.price_per_sqm || 0;

  // Calculator calculations
  const calculateCosts = () => {
    const price = property.price || 0;
    const provizija = price * 0.025;
    const pdvNaProviziju = provizija * 0.25;
    const ppn = price * 0.03;
    const ukupno = provizija + pdvNaProviziju + ppn;
    
    return {
      provizija,
      pdvNaProviziju,
      ppn,
      ukupno
    };
  };

  const costs = calculateCosts();

  if (!isOpen) return null;

  return (
    <>
      <div 
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4"
        onClick={onClose}
      >
        <div 
          className="bg-white/95 backdrop-blur-xl w-full max-w-4xl rounded-2xl shadow-xl overflow-hidden border border-white/50 relative"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button */}
          <button 
            onClick={onClose}
            className="absolute top-4 right-4 z-20 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white text-gray-700 rounded-full transition-all duration-200 cursor-pointer shadow-md"
            aria-label="Zatvori modal"
          >
            <i className="ri-close-line text-xl"></i>
          </button>

          {/* Gallery - Left Side */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 max-h-[85vh] overflow-y-auto">
            <div className="relative bg-gray-900 h-80 lg:h-auto lg:min-h-[450px] lg:max-h-[85vh] flex-shrink-0">
              <img 
                src={images[currentImageIndex]}
                alt={property.image_alts?.[currentImageIndex] || `${property.title} - slika ${currentImageIndex + 1}`}
                className="w-full h-full object-cover"
                loading="lazy"
              />
              
              {/* Navigation Arrows */}
              {images.length > 1 && (
                <>
                  <button
                    onClick={prevImage}
                    className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-all cursor-pointer shadow-md"
                    aria-label="Prethodna slika"
                  >
                    <i className="ri-arrow-left-s-line text-xl text-gray-800"></i>
                  </button>
                  <button
                    onClick={nextImage}
                    className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full transition-all cursor-pointer shadow-md"
                    aria-label="Sljedeća slika"
                  >
                    <i className="ri-arrow-right-s-line text-xl text-gray-800"></i>
                  </button>
                  
                  {/* Image Counter */}
                  <div className="absolute bottom-4 left-1/2 -translate-x-1/2 bg-black/60 backdrop-blur-sm text-white px-3 py-1.5 rounded-full text-sm font-medium">
                    {currentImageIndex + 1} / {images.length}
                  </div>
                </>
              )}
            </div>

            {/* Content - Right Side */}
            <div className="p-6 flex flex-col">
              {/* Type Badge & ID */}
              <div className="flex items-center gap-3 mb-3">
                <span className="bg-red-100 text-red-600 px-3 py-1 rounded-lg text-sm font-semibold">
                  {property.type}
                </span>
                {isRental && (
                  <span className="bg-blue-100 text-blue-600 px-3 py-1 rounded-lg text-sm font-semibold">
                    Najam
                  </span>
                )}
                <span className="text-xs text-gray-500 font-medium">
                  ID: {property.id}
                </span>
              </div>

              {/* Title */}
              <h2 className="text-xl font-bold text-gray-900 mb-2 leading-tight">
                {property.title}
              </h2>

              {/* Location */}
              <div className="flex items-center text-gray-600 mb-3">
                <i className="ri-map-pin-line mr-2 text-red-500"></i>
                <span className="font-medium text-sm">{property.location}</span>
              </div>

              {/* Price */}
              <div className="mb-3">
                <div className="text-2xl font-bold text-red-600 mb-1">
                  €{displayPrice?.toLocaleString()}{isRental ? '/mj' : ''}
                </div>
                {!isRental && (
                  <div className="text-sm text-gray-600 font-medium bg-red-50/80 backdrop-blur-sm px-2 py-1 rounded-lg inline-block">
                    €{pricePerSqm.toLocaleString()}/m²
                  </div>
                )}
              </div>

              {/* Details Icons */}
              <div className="flex items-center gap-4 text-sm text-gray-700 mb-3 pb-3 border-b border-gray-200">
                <div className="flex items-center">
                  <i className="ri-hotel-bed-line mr-1.5 text-red-500 text-2xl"></i>
                  <span className="font-medium">{property.bedrooms}</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-drop-line mr-1.5 text-red-500 text-2xl"></i>
                  <span className="font-medium">{property.bathrooms}</span>
                </div>
                <div className="flex items-center">
                  <i className="ri-ruler-line mr-1.5 text-red-500 text-2xl"></i>
                  <span className="font-medium">{property.area} m²</span>
                </div>
              </div>

              {/* Description */}
              {property.description && (
                <div className="mb-3">
                  <p className={`text-gray-700 leading-relaxed text-sm ${!showFullDescription ? 'line-clamp-3' : ''}`}>
                    {property.description}
                  </p>
                  {property.description.length > 150 && (
                    <button
                      onClick={() => setShowFullDescription(!showFullDescription)}
                      className="text-red-600 hover:text-red-700 text-sm font-medium mt-1 cursor-pointer"
                    >
                      {showFullDescription ? 'Prikaži manje' : 'Prikaži više'}
                    </button>
                  )}
                </div>
              )}

              {/* Features */}
              {property.features && property.features.length > 0 && (
                <div className="mb-4">
                  <div className="flex flex-wrap gap-1.5">
                    {property.features.slice(0, 4).map((feature, index) => (
                      <span 
                        key={index}
                        className="px-2 py-1 bg-gray-100/80 backdrop-blur-sm text-gray-700 rounded-lg text-xs font-medium border border-gray-200/60"
                      >
                        {feature}
                      </span>
                    ))}
                    {property.features.length > 4 && (
                      <span className="px-2 py-1 bg-red-100/80 backdrop-blur-sm text-red-600 rounded-lg text-xs font-medium border border-red-200/60">
                        +{property.features.length - 4} više
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Calculator CTA - Only show for sale properties */}
              {!isRental && (
                <button
                  onClick={() => setShowCalculator(true)}
                  className="w-full bg-white/20 backdrop-blur-xl border border-white/30 text-gray-700 py-3 rounded-xl font-semibold hover:bg-white/30 transition-all duration-200 cursor-pointer whitespace-nowrap shadow-md text-sm mb-3"
                >
                  <i className="ri-calculator-line mr-2"></i>
                  Kalkulator troškova
                </button>
              )}

              {/* CTA */}
              <button
                onClick={onInquiryClick}
                className="w-full bg-red-600 hover:bg-red-700 text-white py-3 rounded-xl font-semibold transition-all duration-200 cursor-pointer whitespace-nowrap shadow-md text-sm mt-auto"
              >
                POŠALJITE UPIT
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Calculator Modal - Only for sale properties */}
      {showCalculator && !isRental && (
        <div 
          className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 backdrop-blur-xl p-4"
          onClick={() => setShowCalculator(false)}
        >
          <div 
            className="bg-white/95 backdrop-blur-xl w-full max-w-md rounded-2xl shadow-2xl border border-white/50 relative p-6"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Close Button */}
            <button 
              onClick={() => setShowCalculator(false)}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white text-gray-700 rounded-full transition-all duration-200 cursor-pointer shadow-md"
              aria-label="Zatvori kalkulator"
            >
              <i className="ri-close-line text-xl"></i>
            </button>

            <h3 className="text-2xl font-bold text-gray-900 mb-6">Kalkulator troškova</h3>

            <div className="space-y-4">
              {/* Cijena nekretnine */}
              <div className="bg-gray-50/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                <div className="text-sm text-gray-600 mb-1">Cijena nekretnine</div>
                <div className="text-xl font-bold text-gray-900">
                  €{property.price?.toLocaleString()}
                </div>
              </div>

              {/* Provizija */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-600">Provizija (2.5%)</div>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    €{costs.provizija.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* PDV na proviziju */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-600">PDV na proviziju (25%)</div>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    €{costs.pdvNaProviziju.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* PPN */}
              <div className="bg-white/80 backdrop-blur-sm rounded-xl p-4 border border-gray-200/60">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-sm text-gray-600">PPN - Porez u prometu (3%)</div>
                  </div>
                  <div className="text-lg font-bold text-gray-900">
                    €{costs.ppn.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>

              {/* Ukupno */}
              <div className="bg-red-50/80 backdrop-blur-sm rounded-xl p-4 border border-red-200/60">
                <div className="flex justify-between items-center">
                  <div>
                    <div className="text-base font-semibold text-red-900">Ukupni troškovi</div>
                  </div>
                  <div className="text-2xl font-bold text-red-600">
                    €{costs.ukupno.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </div>
                </div>
              </div>
            </div>

            <div className="mt-6 text-xs text-gray-500 text-center">
              Prikazani iznosi su informativne prirode
            </div>
          </div>
        </div>
      )}
    </>
  );
}
